import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import type { IncludedAppointment } from "api/services/messaging/schemas/messaging";
import { isAppointmentDisabled } from "routes/calendar/components/Calendar/helpers/transformAppointments";
import { styles } from "shared/atoms/BaseButton/BaseButton";
import { useLocalizedDate } from "utils/date";
import { translateAppointmentMedium, translateAppointmentPurpose, translateAppointmentState } from "utils/translations";

interface AppointmentMessageProps {
  appointment: IncludedAppointment;
}

export const AppointmentMessage: React.FC<AppointmentMessageProps> = ({ appointment }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { format, parseISO } = useLocalizedDate();

  const { end_time, ics_url, medium, purpose, start_time, state_v2 } = appointment;
  const isCanceled = state_v2 === "canceled";
  const isCompleted = state_v2 === "completed";
  const isRescheduled = state_v2 === "rescheduled";

  return (
    <Container className={clsx({ canceled: isCanceled, completed: isCompleted, rescheduled: isRescheduled })}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {state_v2 && (
            <Chip className={clsx({ canceled: isCanceled, completed: isCompleted, rescheduled: isRescheduled })}>
              <Typography variant="label" color="#fff">
                {translateAppointmentState(state_v2)}
              </Typography>
            </Chip>
          )}
          <Typography variant="body_bold">{translateAppointmentPurpose(purpose)}</Typography>
        </Stack>
        {!isAppointmentDisabled(state_v2) && ics_url && (
          <DownloadAppointmentButton download href={ics_url}>
            {t("messages.appointments.calendar_add")}
            <FontAwesomeIcon icon={faCalendar} style={{ height: "16px", width: "16px" }} />
          </DownloadAppointmentButton>
        )}
      </Stack>

      <Stack direction="row" spacing={{ xs: 1, md: 2 }} sx={{ flexWrap: "wrap" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="label" color={theme.colors.redesign.db70}>
            {t("messages.appointments.date")}
          </Typography>
          <Typography variant="subtitle1" color={theme.colors.redesign.db100}>
            {format(parseISO(start_time), "PP")}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="label" color={theme.colors.redesign.db70}>
            {t("messages.appointments.time")}
          </Typography>
          <Typography variant="subtitle1" color={theme.colors.redesign.db100}>
            {`${format(parseISO(start_time), "p")} - ${format(parseISO(end_time), "p")}`}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="label" color={theme.colors.redesign.db70}>
            {t("messages.appointments.type")}
          </Typography>
          <Typography variant="subtitle1" color={theme.colors.redesign.db100}>
            {translateAppointmentMedium(medium)}
          </Typography>
        </Stack>
      </Stack>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.redesign.b10};
  border: 1px solid ${({ theme }) => theme.colors.redesign.b100};
  border-radius: 16px;

  &.canceled {
    background-color: ${({ theme }) => theme.colors.redesign.r10};
    border-color: ${({ theme }) => theme.colors.redesign.r100};
  }

  &.completed {
    background-color: ${({ theme }) => theme.colors.redesign.g10};
    border-color: ${({ theme }) => theme.colors.redesign.g100};
  }

  &.rescheduled {
    background-color: ${({ theme }) => theme.colors.redesign.o10};
    border-color: ${({ theme }) => theme.colors.redesign.o100};
  }
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.redesign.b100};
  border-radius: 4px;

  &.canceled {
    background-color: ${({ theme }) => theme.colors.redesign.r100};
  }

  &.completed {
    background-color: ${({ theme }) => theme.colors.redesign.g100};
  }

  &.rescheduled {
    background-color: ${({ theme }) => theme.colors.redesign.o100};
  }
`;

const DownloadAppointmentButton = styled.a`
  ${styles.action}
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
`;
